import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HmacSHA1 } from 'crypto-js';
import utils from '../features/utils';

import { API } from '../constants/api.constants';

import { AuthSignIn, AXIOS, AxiosService } from '@umanforge/common';
import { UserUmanforge } from 'interfaces/user.interfaces';

import { Input } from '../components/input.component';
import { Button } from '../components/button.component';

import userIcon from '../assets/user.svg';
import passwordIcon from '../assets/lock.svg';


interface LoginProps {
  onClickForgotPassword: () => void;
  navigateChangePassword: () => void;
  onAxiosError: ( error: AxiosError ) => void;
}

export function Login( { onClickForgotPassword, navigateChangePassword, onAxiosError }: LoginProps ) {
  const { t } = useTranslation();

  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ canConnect, setCanConnect ] = useState<boolean>( false );
  const [ errorMessage, setErrorMessage ] = useState<string>( '' );

  const emailRef = useRef<HTMLInputElement>( null );
  const passwordRef = useRef<HTMLInputElement>( null );

  const handleSubmit = ( event: FormEvent ) => {
    event.preventDefault();
    setIsLoading( true );
    setErrorMessage( '' );
    const email = emailRef.current?.value;
    const password = String( passwordRef.current?.value );

    AXIOS.get( API.INFOS_AUTH )
      .then( ( responseInfo: AxiosResponse ) => {
        // eslint-disable-next-line new-cap
        AXIOS.post( API.SIGN_IN, { email, password: HmacSHA1( password, String( responseInfo.data.token ) ).toString() } )
          .then( ( response: AxiosResponse ) => {
            if ( response.status === 200 ) {
              const result = response.data as AuthSignIn;
              if ( result.signin ) {
                const user = result.user as UserUmanforge;
                if ( user ) {
                  if ( user.needChangePassword ) {
                    navigateChangePassword();
                  } else {
                    utils.connectUser( ( response.headers as AxiosHeaders ).get( 'auth-token' ) as string );
                  }
                }
              }
            } else if ( response.status === 202 ) {
              setErrorMessage( AxiosService.getAxiosMessage( response ) );
            }
          } )
          .catch( ( err: AxiosError ) => {
            onAxiosError( err );
          } )
          .finally( () => {
            setIsLoading( false );
          } );
      } )
      .catch( ( err: AxiosError ) => {
        onAxiosError( err );
        setIsLoading( false );
      } );
  };

  const checkValidEmail = ( value: string ): string => {
    let error = '';
    if ( value === '' ) {
      error = 'The field must not be empty !';
    } else if ( !utils.isValidEmail( value ) ) {
      error = 'Please enter an email address !';
    }
    return error;
  };

  const checkValidPassword = ( value: string ): string => {
    let error = '';
    if ( value === '' ) {
      error = 'The field must not be empty !';
    }
    return error;
  };

  const onChange = () => {
    const email = emailRef.current?.value;
    const password = passwordRef.current?.value;
    setCanConnect( checkValidEmail( email ?? '' ) == '' && checkValidPassword( password ?? '' ) === '' );
  };

  return (
    <form
      onSubmit={ handleSubmit }
      className="flex flex-col w-full h-full gap-[30px] items-center justify-center"
    >
      <div className='flex flex-col gap-[20px]'>
        <Input
          ref={ emailRef }
          checkContent={ checkValidEmail }
          onChange={ onChange }
          label={ t( 'Email' ) }
          icon={ userIcon }
          placeholder={ t( 'enter_an_email' ) }
          maxLength={ 320 }
        />
        <div className='flex flex-col gap-[6px]'>
          <Input
            ref={ passwordRef }
            checkContent={ checkValidPassword }
            onChange={ onChange }
            label={ t( 'Password' ) }
            icon={ passwordIcon }
            placeholder={ t( 'common:enter_a_password' ) }
            isPassword={true}
          />
          <div className='flex w-full justify-end'>
            <p
              className='w-fit text-xs text-end text-blue-600 cursor-pointer select-none hover:text-blue-800'
              onClick={ onClickForgotPassword }
            >
              { t( 'Forgot your password ?' ) }
            </p>
          </div>
        </div>
      </div>
      <p className='text-sm text-center text-error h-4' >
        { errorMessage }
      </p>
      <div className='flex justify-center w-full' >
        <div className='w-2/3 min-w-[150px] h-9' >
          <Button
            label={ t( 'Login' ) }
            type='submit'
            isLoading={ isLoading }
            disable={ !canConnect }
          />
        </div>
      </div>
    </form>
  );
}